import { ViewId, KnownOrganizationIds } from "@madhive/mad-sdk";
import { madSDK } from "lib/sdk";
import { selectCurrentUser } from "appReducers/authReducer";
import LoginView from "components/Login";
import SmithersSkeleton from "components/Reusable/SmithersSkeleton/SmithersSkeleton";
import { Routes } from "lib/constants/routes";
import { getUserPermissions } from "components/Auth/utils";
import { useFeatureFlag, RemoteConfigProperties } from "hooks/useFeatureFlag";
import { Routes as RouterRoutes, Route, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { lazy, FC, Suspense, useMemo } from "react";
import { madTracker } from "frontier/lib/tracker";
import { PeerChanges } from "frontier/lib/components/PeerChanges";
import useManageDealsView from "hooks/useManageDealsView";
import useManagePricesView from "hooks/useManagePricesView";
import { getLoginBackgroundClassName } from "./utils";

/**
 * These lazy imports allow us to load only the code necessary for the view the
 * user is looking at.
 */
const LoginLocal = lazy(() => import("components/LoginLocal"));
const NotFound = lazy(() => import("../NotFound"));
const ManageUsers = lazy(
  () => import("../../features/ManageUsers/ManageUsers")
);

const ManageCampaigns = lazy(
  () => import("../../features/ManageCampaigns/ManageCampaigns")
);
const TrackingPixels = lazy(
  () => import("../../features/TrackingPixels/TrackingPixels")
);

const CreativeLibrary = lazy(
  () => import("../../features/CreativeLibrary/CreativeLibrary")
);
const UserProfile = lazy(
  () => import("../../features/UserProfile/UserProfile")
);

const Dashboard = lazy(() => import("../../features/Dashboard/Dashboard"));

const ManageAdvertisers = lazy(
  () => import("../../features/ManageAdvertisers/ManageAdvertisers")
);

const ManageAgencies = lazy(() => import("../../features/ManageAgencies"));

const ManageStations = lazy(
  () => import("../../features/ManageStations/ManageStations")
);

const ManageProducts = lazy(
  () => import("../../features/ManageProducts/ManageProducts")
);

const ManageDeals = lazy(() => import("../../frontier/routes/internal/deals"));

const PublisherManager = lazy(
  () => import("../../frontier/routes/internal/publisher-manager")
);

const Pricing = lazy(() => import("../../frontier/routes/internal/pricing"));

const ManagePublisherGroups = lazy(
  () => import("../../frontier/routes/internal/publisher-groups")
);

const PremionPacing = lazy(
  () => import("../../features/PremionPacing/PremionPacing")
);

const AdbookErrors = lazy(
  () => import("../../features/ScrippsOnly/AdbookErrors/AdbookErrors")
);

const Pacing = lazy(() => import("../../features/Pacing"));

const ManageAudiences = lazy(() => import("../../features/ManageAudiences"));

const AudienceDataProvisioning = lazy(
  () => import("../../features/AudienceDataProvisioning")
);

const CustomReporting = lazy(() => import("../../features/CustomReporting"));

const CryptoDashboard = lazy(
  () => import("../../features/CryptoDashboard/page")
);

const DataStudioReports = lazy(
  () => import("../../features/DataStudioReports/DataStudioReports")
);

const ManagePublishers = lazy(
  () => import("../../features/ManagePublishers/ManagePublishers")
);

const WideOrbitErrors = lazy(
  () => import("../../features/WideOrbit/WideOrbitErrors")
);

// Talon Route
const OOHPlanning = lazy(() => import("../../features/OOH/Planning"));

const Planner = lazy(() => import("../../features/Planner"));

const PlannerAdminTool = lazy(
  () => import("../../frontier/lib/planner-admin-tool")
);

const OrgSettings = lazy(
  () => import("../../frontier/routes/internal/org-settings")
);

const AdvancedExports = lazy(() => import("../../features/AdvancedExports"));

const FootTrafficAttribution = lazy(
  () => import("../../features/FootTrafficAttribution")
);

const FirstPartyDataOnboarding = lazy(
  () => import("../../features/FirstPartyDataOnboarding")
);

const IASKeywordsUploader = lazy(
  () => import("../../features/IASKeywordsUploader")
);

const Admin = lazy(() => import("../../frontier/routes/admin"));

const FiveMinSeparation = lazy(
  () => import("../../frontier/routes/internal/five-min-separation")
);

const ConversionDashboard = lazy(
  () => import("../../features/ConversionDashboard")
);

const Spend = lazy(() => import("../../frontier/routes/insights/spend"));

const Conversion = lazy(
  () => import("../../frontier/routes/insights/conversion")
);

const ReportBuilder = lazy(
  () => import("../../frontier/routes/insights/report-builder")
);

const Reports = lazy(() => import("../../frontier/routes/insights/reports"));

const PacingDashboard = lazy(
  () => import("../../frontier/routes/insights/pacing")
);

const InsightsDashboard = lazy(
  () => import("../../frontier/routes/insights/insights-dashboard")
);

const OverviewDashboard = lazy(
  () => import("../../frontier/routes/insights/overview-dashboard")
);

const ExternalOverviewDashboard = lazy(
  () => import("../../frontier/routes/insights/external-overview-dashboard")
);

const DmpDashboard = lazy(() => import("../../frontier/lib/dmp-dashboard"));

const MaverickAISegments = lazy(
  () => import("../../frontier/lib/maverick-ai-segments")
);

const MaverickAISegmentsExternal = lazy(
  () => import("../../frontier/lib/maverick-ai-segments-external")
);

const ManageAiAgents = lazy(
  () => import("../../frontier/lib/ai-agents/ManageAgents.Router")
);

const AiMode = lazy(() => import("../../frontier/routes/ai-mode/ai.routes"));

const Experiments = lazy(() => import("../../frontier/lib/experiments"));

const ExperimentMeta = lazy(() => import("../../frontier/lib/experiment-meta"));

const PromotedDashboards = lazy(
  () => import("../../frontier/lib/prism/promoted-dashboards")
);

const SellerLandingPage = lazy(
  () => import("../../frontier/lib/seller/pages/SellerLandingPage")
);

const ProposalsListPage = lazy(
  () => import("../../frontier/lib/seller/pages/ProposalsListPage")
);

const ProposalIntakePage = lazy(
  () => import("../../frontier/lib/seller/pages/ProposalIntakePage")
);

const NewProposalPage = lazy(
  () => import("../../frontier/lib/seller/pages/NewProposalPage")
);

const ProposalSnapshotPage = lazy(
  () => import("../../frontier/lib/seller/pages/ProposalSnapshotPage")
);

const AiCreativeLandingPage = lazy(
  () =>
    import("../../frontier/lib/maverick-creative/pages/AiCreativeLandingPage")
);

const AiCreativeLibraryPage = lazy(
  () =>
    import("../../frontier/lib/maverick-creative/pages/AiCreativeLibraryPage")
);

const ClientsListPage = lazy(
  () => import("../../frontier/lib/seller/pages/ClientsListPage")
);

const ClientDiscoveryPage = lazy(
  () => import("../../frontier/lib/seller/pages/ClientDiscoveryPage")
);

const SmithersRouter: FC = () => {
  const user = useSelector(selectCurrentUser);
  const { hostname } = window.location;

  const location = useLocation();
  useMemo(() => {
    madTracker.page();
  }, [location.hash, location.pathname, location.search]);

  const canViewManageDeals = useManageDealsView();

  const { canView: canViewFiveMinSeparation } = getUserPermissions(
    ViewId.FIVE_MIN_SEPARATION,
    user?.abilities || []
  );

  const isFiveMinSeparationEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.INTERNAL_FIVE_MIN_SEPARATION
  );

  const { canView: canViewOrgSettings } = getUserPermissions(
    ViewId.ORG_SETTINGS,
    user?.abilities || []
  );

  const isOrgSettingsEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.ORG_SETTINGS
  );

  const isPublisherMangerEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.PUBLISHER_MANAGER
  );

  const canViewManagePrices = useManagePricesView();

  const { canView: canViewSpend } = getUserPermissions(
    ViewId.SPEND_DASHBOARD,
    user?.abilities || []
  );

  const isAudienceDataProvisioningEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.ENABLE_AUDIENCE_DATA_PROVISIONING
  );

  // Only Madhive admin users can access Audience Data Provisioning
  const canViewAudienceDataProvisioning =
    user?.primaryOrganizationId === KnownOrganizationIds.MADHIVE_DEFAULT &&
    user?.isAdmin;

  const isBiConversionDashboardEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.ENABLE_BI_CONVERSION_DASHBOARD
  );

  const { canView: canViewConversion } = getUserPermissions(
    ViewId.BI_CONVERSION_DASHBOARD,
    user?.abilities || []
  );

  const { canView: canViewPacing } = getUserPermissions(
    ViewId.PACING_BETA,
    user?.abilities || []
  );

  const isBiSpendDashboardEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.ENABLE_BI_SPEND_DASHBOARD
  );

  const isSaveAlertsFeatureEnabled = useFeatureFlag(
    RemoteConfigProperties.PEER_CHANGES_SAVE_ALERTS
  );

  const isPublisherGroupsV2Enabled: boolean = useFeatureFlag(
    RemoteConfigProperties.PUBLISHER_GROUPS_V2
  );

  const isDmpDashboardEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.AUDIENCES_DMP_DASHBOARD
  );

  const isIASKeywordsUploaderEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.IAS_KEYWORD_TARGETING
  );

  // Only Madhive admin users can access Maverick AI Segments
  const canViewMaverickAISegments =
    user?.primaryOrganizationId === KnownOrganizationIds.MADHIVE_DEFAULT &&
    user?.isAdmin;

  const isMaverickExternalEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.MAVERICK_AI_SEGMENTS_EXTERNAL_UI
  );

  // Only Madhive dev users can access the Manage AI Agents view, which is currently an internal tool.
  const canManageAiAgents = user?.isDev;

  const isPromotedDashboardsNavEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.PROMOTED_DASHBOARDS_REPORTING_NAV
  );

  // Devs always get the page (Internal Tools); clients get it via the flag.
  const canViewPromotedDashboards =
    user?.isDev || isPromotedDashboardsNavEnabled;

  // Only the root user (dev@madhive.com) can access Experiments
  const canViewExperiments = madSDK.isRootUser();

  // campaignmanager's configuration-meta endpoints authorize only the root
  // user, same as Experiments -- keep this in sync with canViewExperiments.
  const canViewExperimentMeta = madSDK.isRootUser();

  const isMaverickSellerEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.MAVERICK_SELLER
  );

  // Gated independently from MAVERICK_SELLER for partner-level pilot rollout.
  const isAiCreativeEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.MAVERICK_AI_CREATIVE
  );

  return (
    <Suspense fallback={<SmithersSkeleton.Page fullContent />}>
      <div
        className={classNames(
          "Smithers__FillContainer",
          getLoginBackgroundClassName(hostname, user)
        )}
      >
        {isSaveAlertsFeatureEnabled && <PeerChanges />}
        <RouterRoutes>
          <Route path={Routes.LOGIN} element={<LoginView />} />
          <Route path={Routes.LOGIN_LOCAL} element={<LoginLocal />} />
          <Route path={`${Routes.DASHBOARD}/*`} element={<Dashboard />} />
          <Route
            path={`${Routes.CONVERSION_DASHBOARD}/*`}
            element={<ConversionDashboard />}
          />
          <Route path={Routes.HOME} element={<Dashboard />} />
          <Route path={Routes.USER_PROFILE} element={<UserProfile />} />
          <Route path={`${Routes.MANAGE_USERS}/*`} element={<ManageUsers />} />
          {isPublisherGroupsV2Enabled && (
            <Route
              path={`${Routes.MANAGE_PUBLISHER_GROUPS}/*`}
              element={<ManagePublisherGroups />}
            />
          )}
          <Route
            path={`${Routes.MANAGE_CAMPAIGNS}/*`}
            element={<ManageCampaigns />}
          />
          <Route
            path={`${Routes.TRACKING_PIXELS}/*`}
            element={<TrackingPixels />}
          />
          <Route
            path={`${Routes.CREATIVE_LIBRARY}/*`}
            element={<CreativeLibrary />}
          />
          <Route path="*" element={<NotFound />} />
          <Route path={`${Routes.PACING}/*`} element={<Pacing />} />
          {(canViewPacing || user?.isDev) && (
            <Route
              path={`${Routes.PACING_BETA}/*`}
              element={<PacingDashboard />}
            />
          )}
          <Route
            path={`${Routes.MANAGE_ADVERTISERS}/*`}
            element={<ManageAdvertisers />}
          />
          <Route
            path={`${Routes.MANAGE_AGENCIES}/*`}
            element={<ManageAgencies />}
          />
          <Route
            path={`${Routes.MANAGE_STATIONS}/*`}
            element={<ManageStations />}
          />
          <Route
            path={`${Routes.MANAGE_PRODUCTS}/*`}
            element={<ManageProducts />}
          />
          {canViewManageDeals && (
            <Route
              path={`${Routes.MANAGE_DEALS}/*`}
              element={<ManageDeals />}
            />
          )}
          {isPublisherMangerEnabled && (
            <Route
              path={`${Routes.PUBLISHER_MANAGER}/*`}
              element={<PublisherManager />}
            />
          )}
          {canViewManagePrices && (
            <Route path={`${Routes.MANAGE_PRICES}/*`} element={<Pricing />} />
          )}
          {isFiveMinSeparationEnabled &&
            (canViewFiveMinSeparation || user?.isDev) && (
              <Route
                path={`${Routes.FIVE_MIN_SEPARATION}/*`}
                element={<FiveMinSeparation />}
              />
            )}
          {((isBiSpendDashboardEnabled && canViewSpend) || user?.isDev) && (
            <Route path={`${Routes.SPEND_DASHBOARD}/*`} element={<Spend />} />
          )}
          {isBiConversionDashboardEnabled &&
            (canViewConversion || user?.isDev) && (
              <Route
                path={`${Routes.BI_CONVERSION_DASHBOARD}/*`}
                element={<Conversion />}
              />
            )}
          <Route
            path={`${Routes.DELIVERY_REPORT}/*`}
            element={<PremionPacing />}
          />
          <Route
            path={`${Routes.ADBOOK_ERRORS}/*`}
            element={<AdbookErrors />}
          />
          <Route
            path={`${Routes.MANAGE_AUDIENCES}/*`}
            element={<ManageAudiences />}
          />
          {isAudienceDataProvisioningEnabled &&
            canViewAudienceDataProvisioning && (
              <Route
                path={`${Routes.AUDIENCE_DATA_PROVISIONING}/*`}
                element={<AudienceDataProvisioning />}
              />
            )}
          <Route
            path={`${Routes.CUSTOM_REPORTING}/*`}
            element={<CustomReporting />}
          />
          <Route
            path={`${Routes.CRYPTO_DASHBOARD}/*`}
            element={<CryptoDashboard />}
          />
          <Route path={`${Routes.OOH_PLANNING}/*`} element={<OOHPlanning />} />
          <Route
            path={`${Routes.DATASTUDIO_REPORTS}/*`}
            element={<DataStudioReports />}
          />
          <Route
            path={`${Routes.MANAGE_PUBLISHERS}/*`}
            element={<ManagePublishers />}
          />
          <Route
            path={`${Routes.WIDE_ORBIT_ERRORS}/*`}
            element={<WideOrbitErrors />}
          />
          <Route path={`${Routes.PLANNER}/*`} element={<Planner />} />
          <Route
            path={`${Routes.PLANNER_ADMIN_TOOL}/*`}
            element={<PlannerAdminTool />}
          />
          {isOrgSettingsEnabled && (canViewOrgSettings || user?.isDev) && (
            <Route
              path={`${Routes.ORG_SETTINGS}/*`}
              element={<OrgSettings />}
            />
          )}
          {isDmpDashboardEnabled && (
            <Route
              path={`${Routes.DMP_DASHBOARD}/*`}
              element={<DmpDashboard />}
            />
          )}
          {canViewMaverickAISegments && (
            <Route
              path={`${Routes.MAVERICK_AI_SEGMENTS}/*`}
              element={<MaverickAISegments />}
            />
          )}
          {isMaverickExternalEnabled && (
            <Route
              path={`${Routes.MAVERICK_AI_SEGMENTS_EXTERNAL}/*`}
              element={<MaverickAISegmentsExternal />}
            />
          )}
          <Route
            path={`${Routes.ADVANCED_EXPORTS}/*`}
            element={<AdvancedExports />}
          />
          <Route
            path={`${Routes.WIDE_ORBIT_ERRORS}/*`}
            element={<WideOrbitErrors />}
          />
          <Route path={`${Routes.PLANNER}/*`} element={<Planner />} />
          <Route
            path={`${Routes.ADVANCED_EXPORTS}/*`}
            element={<AdvancedExports />}
          />
          <Route
            path={`${Routes.FOOT_TRAFFIC_ATTRIBUTION}/*`}
            element={<FootTrafficAttribution />}
          />
          <Route
            path={`${Routes.FIRST_PARTY_DATA_ONBOARDING}/*`}
            element={<FirstPartyDataOnboarding />}
          />
          {isIASKeywordsUploaderEnabled && (
            <Route
              path={`${Routes.IAS_KEYWORDS_UPLOADER}/*`}
              element={<IASKeywordsUploader />}
            />
          )}
          <Route path={`${Routes.ADMIN}/*`} element={<Admin />} />

          <Route
            path={`${Routes.REPORT_BUILDER}/*`}
            element={<ReportBuilder />}
          />
          <Route path={`${Routes.REPORT_CENTER}/*`} element={<Reports />} />
          <Route
            path={`${Routes.INSIGHTS_DASHBOARD}/*`}
            element={<InsightsDashboard />}
          />
          <Route
            path={Routes.OVERVIEW_DASHBOARD_EXTERNAL}
            element={<ExternalOverviewDashboard />}
          />
          <Route
            path={`${Routes.OVERVIEW_DASHBOARD}/*`}
            element={<OverviewDashboard />}
          />

          {canManageAiAgents && (
            <Route
              path={`${Routes.MANAGE_AI_AGENTS}/*`}
              element={<ManageAiAgents />}
            />
          )}

          <Route path={`${Routes.AI_MODE}/*`} element={<AiMode />} />

          {canViewExperiments && (
            <Route
              path={`${Routes.MANAGE_EXPERIMENTS}/*`}
              element={<Experiments />}
            />
          )}

          {canViewExperimentMeta && (
            <Route
              path={`${Routes.MANAGE_EXPERIMENT_META}/*`}
              element={<ExperimentMeta />}
            />
          )}

          {canViewPromotedDashboards && (
            <Route
              path={Routes.PROMOTED_DASHBOARDS}
              element={<PromotedDashboards />}
            />
          )}

          {isMaverickSellerEnabled && (
            <Route path={Routes.MAVERICK_SELLER}>
              <Route index element={<SellerLandingPage />} />
              <Route path="proposals" element={<ProposalsListPage />} />
              <Route path="proposals/new" element={<ProposalIntakePage />} />
              <Route
                path="proposals/new/builder"
                element={<NewProposalPage />}
              />
              <Route
                path="proposals/new/snapshot"
                element={<ProposalSnapshotPage />}
              />
              {/* react-router ranks branches by specificity, so the literal
                  `proposals/new*` paths beat this dynamic one wherever it sits.
                  Declared here to read in order; the order is cosmetic. */}
              <Route
                path="proposals/:proposalId"
                element={<ProposalSnapshotPage />}
              />
              <Route path="clients" element={<ClientsListPage />} />
              <Route path="clients/new" element={<ClientDiscoveryPage />} />
              <Route
                path="clients/:clientId"
                element={<ClientDiscoveryPage />}
              />
            </Route>
          )}

          {isAiCreativeEnabled && (
            <Route
              path={Routes.MAVERICK_AI_CREATIVE}
              element={<AiCreativeLandingPage />}
            />
          )}

          {isAiCreativeEnabled && (
            <Route
              path={Routes.MAVERICK_AI_CREATIVE_LIBRARY}
              element={<AiCreativeLibraryPage />}
            />
          )}
        </RouterRoutes>
      </div>
    </Suspense>
  );
};

export default SmithersRouter;
